import type { Vec2, Ring } from '../../../geo/types';
import type { LinearRGB } from '../../../core/color';
import type { Rng } from '../../../core/rng';
import type { Landmark } from '../../../data/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { centroid } from '../../../geo/polygon';
import { horizontalCap } from '../solids';
import { hipRoof } from '../roofSolids';
import { emitFacade, emitShapedWall, type FacadeContext } from '../houseFacades';
import type { DetailSink } from '../detailSink';
import { readNumber } from './params';
import { dominantAxis, meshFrameOf, planBounds, planFrameOf, type PlanFrame } from './planFrame';
import { edgeFace, rectWall, type MasonryStyles } from './masonry';
import { arcadeArches, buildArcade, type Arch } from './arcade';
import { buildSquareTower, type SquareTower } from './churchTower';
import { civicOpenings, storeyLineNear } from './civicOpenings';

const DEG = Math.PI / 180;

export const TOWNHALL = {
  height: 12,
  pitch: 42 * DEG,
  arcadeShare: 0.42,
  loggiaDepth: 3,
  arcadeThickness: 0.8,
  towerWidth: 4.2,
  towerRise: 5.5,
  spireRatio: 3.2,
} as const;

export interface TownhallParams {
  readonly height: number;
  readonly towerWidth: number;
}

export function parseTownhallParams(landmark: Landmark, warn: (msg: string) => void): TownhallParams {
  return {
    height: readNumber(landmark.params, 'height', TOWNHALL.height, warn),
    towerWidth: readNumber(landmark.params, 'towerWidth', TOWNHALL.towerWidth, warn),
  };
}

export interface TownhallColors {
  readonly plaster: LinearRGB;
  readonly stone: LinearRGB;
  readonly roof: LinearRGB;
  readonly dark: LinearRGB;
  /** Window glass and door leaves, as on the houses. */
  readonly glass: LinearRGB;
  readonly leaf: LinearRGB;
}

/** Where the hall stands and what it hands on: the squares it faces and its window details. */
export interface TownhallContext {
  readonly squares: readonly Ring[];
  readonly details: DetailSink;
  readonly rng: Rng;
}

/** The hall in its own plan: long axis along u, the arcade on the +v side facing the square. */
export interface TownhallPlan {
  readonly frame: PlanFrame;
  readonly u0: number;
  readonly u1: number;
  readonly v0: number;
  readonly v1: number;
  readonly eaveY: number;
  readonly ridgeY: number;
  readonly pitch: number;
  readonly arcadeY: number;
  readonly loggiaDepth: number;
  /** Arches along the front edge, measured from its start at u1 toward u0. */
  readonly arcade: readonly Arch[];
  /** The same arches as plan u-ranges. */
  readonly arches: readonly { readonly u0: number; readonly u1: number }[];
  readonly tower: SquareTower;
}

function facingFrame(landmark: Landmark, squares: readonly Ring[]): PlanFrame {
  const axis = dominantAxis(landmark.footprint, landmark.heading);
  const first = planFrameOf(landmark.footprint, axis);
  const b = planBounds(first);
  const along = b.u1 - b.u0 >= b.v1 - b.v0 ? axis : axis + Math.PI / 2;
  const frame = planFrameOf(landmark.footprint, along);
  if (squares.length === 0) return frame;
  const near = squares
    .map((ring) => centroid(ring))
    .reduce((a, c) => (dist(c, frame.origin) < dist(a, frame.origin) ? c : a));
  const toward = -(near[0] - frame.origin[0]) * Math.sin(along) + (near[1] - frame.origin[1]) * Math.cos(along);
  return toward >= 0 ? frame : planFrameOf(landmark.footprint, along + Math.PI);
}

function dist(a: Vec2, b: Vec2): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

/** Arches along the front edge, which runs from u1 back to u0, as plan u-ranges. */
function frontArches(plan: { u0: number; u1: number }, arches: readonly Arch[]) {
  return arches.map((a) => ({ u0: plan.u1 - a.center - a.width / 2, u1: plan.u1 - a.center + a.width / 2 }));
}

/**
 * The front opens a window row in every storey above the arcade; an arcade top between storey
 * lines would leave a storey cut short over it, so it snaps to the nearest line.
 */
function arcadeHeight(eaveY: number): number {
  return storeyLineNear(eaveY, TOWNHALL.arcadeShare);
}

export function townhallPlanOf(landmark: Landmark, params: TownhallParams, squares: readonly Ring[]): TownhallPlan {
  const frame = facingFrame(landmark, squares);
  const { u0, u1, v0, v1 } = planBounds(frame);
  const eaveY = params.height;
  const arcadeY = arcadeHeight(eaveY);
  const ridgeY = eaveY + (Math.min(u1 - u0, v1 - v0) / 2) * Math.tan(TOWNHALL.pitch);
  const half = params.towerWidth / 2;
  const tower: SquareTower = {
    center: [(u0 + u1) / 2, (v0 + v1) / 2],
    half,
    y0: eaveY,
    topY: ridgeY + TOWNHALL.towerRise,
    spire: 'needle',
    spireHeight: TOWNHALL.spireRatio * params.towerWidth,
  };
  const arcade = arcadeArches(u1 - u0, arcadeY);
  const arches = frontArches({ u0, u1 }, arcade);
  const pitch = TOWNHALL.pitch;
  const loggiaDepth = TOWNHALL.loggiaDepth;
  return { frame, u0, u1, v0, v1, eaveY, ridgeY, pitch, arcadeY, loggiaDepth, arcade, arches, tower };
}

interface HallStyles {
  readonly plaster: (width: number) => MasonryStyles;
  readonly stone: MasonryStyles;
  readonly dark: VertexStyle;
  readonly glass: VertexStyle;
  readonly leaf: VertexStyle;
}

function hallStyles(colors: TownhallColors, plan: TownhallPlan, edgeId: number, seed: number): HallStyles {
  const base = { edgeId, seed, strokeAngle: 0 };
  const dark: VertexStyle = { ...base, color: colors.dark, surface: SURFACE.linework, extent: [1, 1] };
  const roof: VertexStyle = {
    ...base,
    color: colors.roof,
    surface: SURFACE.roofSlate,
    extent: [plan.u1 - plan.u0, (plan.ridgeY - plan.eaveY) / Math.sin(plan.pitch)],
  };
  const stone: VertexStyle = { ...base, color: colors.stone, surface: SURFACE.stone, extent: [10, plan.eaveY] };
  return {
    plaster: (width) => ({
      wall: { ...base, color: colors.plaster, surface: SURFACE.plaster, extent: [width, plan.eaveY] },
      roof,
      opening: dark,
    }),
    stone: { wall: stone, roof, opening: dark },
    dark,
    glass: { ...base, color: colors.glass, surface: SURFACE.linework, extent: [0, 0] },
    leaf: { ...base, color: colors.leaf, surface: SURFACE.wood, extent: [0, 0] },
  };
}

/** One hall under construction: where it stands, how it is styled and where its details go. */
interface Hall {
  readonly builder: MeshBuilder;
  readonly frame: Frame;
  readonly styles: HallStyles;
  readonly plan: TownhallPlan;
  readonly context: TownhallContext;
  readonly edgeId: number;
}

function facadeOf(hall: Hall, width: number): FacadeContext {
  const { builder, frame, styles, edgeId } = hall;
  const facade = { wall: styles.plaster(width).wall, glass: styles.glass, leaf: styles.leaf };
  return { builder, frame, details: hall.context.details, edgeId, styles: facade };
}

/**
 * A plaster wall from y0 to the eaves, opened on every storey that stands wholly within it. The
 * arcade is the hall's entrance, so none of its walls has a door.
 */
function openWall(hall: Hall, edge: readonly [Vec2, Vec2], y0: number, label: string): void {
  const { eaveY } = hall.plan;
  const length = dist(edge[0], edge[1]);
  const openings = civicOpenings({ length, y0, y1: eaveY, eaveY, door: false, keepClear: [] }, hall.context.rng.fork(label));
  const size = { width: length, eaves: eaveY - y0, ridge: eaveY - y0 };
  const wall = openings.map((o) => ({ ...o, y: o.y - y0 }));
  emitFacade(facadeOf(hall, length), edgeFace(edge[0], edge[1], y0), size, { wall, gable: null });
}

/** End wall with the loggia's open side cut out of its foot at the arcade end, windows kept clear of it. */
function endWall(hall: Hall, edge: readonly [Vec2, Vec2], loggiaFirst: boolean): void {
  const { v0, v1, arcadeY, eaveY, loggiaDepth: d } = hall.plan;
  const len = v1 - v0;
  const t = TOWNHALL.arcadeThickness;
  const [x0, x1] = loggiaFirst ? [t, d] : [len - d, len - t];
  const outline: Ring = [[0, 0], [x0, 0], [x0, arcadeY], [x1, arcadeY], [x1, 0], [len, 0], [len, eaveY], [0, eaveY]];
  const keepClear = [{ x0, x1, y0: 0, y1: arcadeY }];
  const rng = hall.context.rng.fork(loggiaFirst ? 'end:start' : 'end:end');
  const openings = civicOpenings({ length: len, y0: 0, y1: eaveY, eaveY, door: false, keepClear }, rng);
  emitShapedWall(facadeOf(hall, len), edgeFace(edge[0], edge[1]), outline, openings);
}

function buildLoggia(hall: Hall): void {
  const { builder, frame, styles, plan } = hall;
  const { u0, u1, v1, arcadeY, loggiaDepth: d } = plan;
  const t = TOWNHALL.arcadeThickness;
  const dark = { wall: styles.dark, roof: styles.dark, opening: styles.dark };
  rectWall(builder, dark, frame, [[u1, v1 - d], [u0, v1 - d]], [0, arcadeY]);
  const ceiling: Ring = [[u0, v1 - d], [u1, v1 - d], [u1, v1 - t], [u0, v1 - t]];
  horizontalCap(builder, styles.dark, frame, ceiling, arcadeY, false);
  const faces = { front: styles.stone.wall, back: styles.dark, soffit: styles.stone.wall };
  buildArcade(builder, faces, frame, [[u1, v1], [u0, v1]], { thickness: t, height: arcadeY }, plan.arcade);
}

function buildBody(hall: Hall): void {
  const { u0, u1, v0, v1, eaveY, arcadeY, pitch } = hall.plan;
  openWall(hall, [[u0, v0], [u1, v0]], 0, 'back');
  endWall(hall, [[u1, v0], [u1, v1]], false);
  openWall(hall, [[u1, v1], [u0, v1]], arcadeY, 'front');
  endWall(hall, [[u0, v1], [u0, v0]], true);
  buildLoggia(hall);
  hipRoof(hall.builder, hall.styles.stone.roof, hall.frame, { u0, u1, v0, v1, eaveY, pitch });
}

export function buildTownhall(
  builder: MeshBuilder,
  landmark: Landmark,
  params: TownhallParams,
  colors: TownhallColors,
  context: TownhallContext,
  ids: { readonly edgeId: number; readonly seed: number }
): number {
  const plan = townhallPlanOf(landmark, params, context.squares);
  const frame = meshFrameOf(plan.frame);
  const styles = hallStyles(colors, plan, ids.edgeId, ids.seed);
  const before = builder.triangleCount;
  buildBody({ builder, frame, styles, plan, context, edgeId: ids.edgeId });
  buildSquareTower(builder, styles.stone, frame, plan.tower);
  return builder.triangleCount - before;
}
